import { useTranslation } from 'react-i18next';
import {
  useAssistantCommentExamMutation,
  useAssistantCreateExamMutation,
  useAssistantDeleteExamMutation,
  useLeadGetExaminationsQuery,
} from '../../../reduxToolkit/reducers/usersSlice';
import { useEffect, useRef, useState } from 'react';
import FutureExaminations from './FutureExaminations';
import PastExaminations from './PastExaminations';
import { Card, CardBody, Container, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import Breadcrumbs from '../../../coreComponents/breadcrumbs';
import CommentFileModal, { PreviewItem } from '../../commetFileModal';
import { validateImages } from '../../../lib/filesUtils';
import { AssistantExamResponse, ExamFile, ExamHistoryItem } from '../../../types/Exams';
import UnsetExaminations from './UnsetExaminations';
import Swal from 'sweetalert2';
import { Primary, Secondary } from '../../../constants';
import { useNavigate } from 'react-router-dom';
import { cleanupBodyLocks } from '../../uiKits/modal/common/CommonModal';
import { AssistantExamDataCreate } from '../../../types/Tables';
import { formDefaultsInit } from '../../courses/examList';
import SimpleModal from '../../uiKits/modal/basicModal/SimpleModal';
import CreateExamForm from '../../courses/assistantCreateExam/CreateExamForm';
import { getPrompts } from '../../../services/api';
import { isNewPreview } from '../../../utils';
import { ExaminationsHeadingTooltip } from '../components/ExaminationsHeadingTooltip';

const examinationsTabs = ['unset', 'future', 'past'];

const Examinations = () => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [basicTab, setBasicTab] = useState('1');
  const [tabsTotals, setTabsTotals] = useState({ unset: 0, future: 0, past: 0 });
  const [comment, setComment] = useState('');
  const [examId, setExamId] = useState<number | null>(null);
  const [currentDetailsId, setCurrentDetailsId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [previews, setPreviews] = useState<PreviewItem[]>([]);
  const [toDeleteFileUrl, setToDeleteFileUrl] = useState<string[] | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isExamPassed, setIsExamPassed] = useState<boolean | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [formDefaults, setFormDefaults] =
    useState<Partial<AssistantExamDataCreate>>(formDefaultsInit);
  const makeAbs = (rel?: string) =>
    rel
      ? `${process.env.REACT_APP_BACKEND_STATIC}/crm/${rel}`
      : 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  const [questionPrompt, setQuesionPrompt] = useState({ ru: '', uk: '' });
  const [commentHandler] = useAssistantCommentExamMutation();
  const [deleteExam] = useAssistantDeleteExamMutation();
  const [createAssistantExam] = useAssistantCreateExamMutation();
  const [isOpenAddExamForm, setIsOpenAddExamForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedTabs = localStorage.getItem('stored-tabs') ?? 1;
    setBasicTab(String(storedTabs));
  }, []);

  const handleModalOpen = (
    exam_id: number,
    examComment: string,
    detailsId: number | null,
    existingFiles?: ExamFile[] | null | undefined,
    examPass?: boolean | null,
  ) => {
    setExamId(exam_id);
    setComment(examComment ?? '');
    setCurrentDetailsId(detailsId);

    // Map existing server-side screenshots to previews
    const existingPreviews: PreviewItem[] =
      (existingFiles ?? []).map((f) => ({
        kind: 'existing',
        url: makeAbs(f.url),
        name: f.name,
      })) ?? [];

    if (examPass) setIsExamPassed(examPass);
    setUploadErrors([]);
    setPreviews(existingPreviews); // reset to existing for this exam
    setOpenModal(true);
  };
  useEffect(() => {
    const getQuestionPrompt = async () => {
      const requests = await getPrompts();
      setQuesionPrompt({ ru: requests[0].questionRequest, uk: requests[0].questionRequest_uk });
    };
    getQuestionPrompt();
  }, []);
  const revokeObjectUrls = (arr: PreviewItem[]) => {
    arr.forEach((p) => {
      if (isNewPreview(p)) URL.revokeObjectURL(p.url);
    });
  };

  const handleModalClose = () => {
    revokeObjectUrls(previews);
    setOpenModal(false);
    setComment('');
    setPreviews([]); // clear after close
    setUploadErrors([]);
  };

  const handleSubmitComment = async () => {
    if (!examId) return;
    const fd = new FormData();
    fd.append('examId', String(examId));
    fd.append('comment', comment);
    if (currentDetailsId != null) fd.append('currentDetailsId', String(currentDetailsId));
    const files = previews.filter(isNewPreview).map((p) => p.file);
    files.forEach((file) => fd.append('screenshots', file));
    fd.append('examPassed', String(isExamPassed));
    fd.append('toDeleteFileUrls', JSON.stringify(toDeleteFileUrl ?? []));
    try {
      setSubmitting(true);
      await commentHandler(fd).unwrap();
      revokeObjectUrls(previews);
      setPreviews((prev) => prev.filter((p) => p.kind === 'existing'));
    } catch (error) {
      console.log('error', error);
    } finally {
      setToDeleteFileUrl(null);
      setSubmitting(false);
      setOpenModal(false);
    }
  };
  const handleFilesSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    const { valid, errors } = validateImages(files, t);
    setUploadErrors(errors);

    if (valid.length) {
      const newPreviews: PreviewItem[] = valid.map((file) => ({
        kind: 'new',
        file,
        url: URL.createObjectURL(file),
      }));
      setPreviews((prev) => [...prev, ...newPreviews]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePreview = (p: PreviewItem) => {
    if (isNewPreview(p)) {
      URL.revokeObjectURL(p.url);
    }
    setPreviews((prev) => prev.filter((x) => x !== p));
    // If you later support removing server files, do it here (optimistic + rollback)
  };

  useEffect(() => {
    let cancelled = false;

    previews.forEach((p) => {
      if (p.w && p.h) return;
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        setPreviews((prev) => {
          const ix = prev.findIndex((x) => x.url === p.url);
          if (ix === -1) return prev;
          const copy = [...prev];
          copy[ix] = {
            ...copy[ix],
            w: img.naturalWidth || 1600,
            h: img.naturalHeight || 1200,
          };
          return copy;
        });
      };
      img.src = p.url;
    });

    return () => {
      cancelled = true;
    };
  }, [previews]);
  const deleteExamCalendarHandler = async (data: any) => {
    try {
      const payload = {
        examId: data.examId,
        userId: data.userId,
        examDate: data.examDate,
      };
      const res = await deleteExam(payload).unwrap();
      if (res.success) {
        Swal.fire({
          title: t('courses.exam.calendar.eventDeleteTitle'),
          text: t('courses.exam.calendar.eventDeleteText'),
          icon: 'success',
          confirmButtonColor: Primary,
        });
      } else if (!res.success) {
        Swal.fire({
          title: 'Error!',
          text: 'Exam delete failed',
          icon: 'error',
          confirmButtonColor: Primary,
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Exam delete failed',
        icon: 'error',
        confirmButtonColor: Primary,
      });
    }
  };

  const handleOpenAddExamForm = (data: any) => {
    const formDefaults = {
      id: data.userId,
      name: data.userName,
      courseId: data.courseId,
      lessonId: data.lessonId,
      date: data.examDate,
      prompt: questionPrompt,
      parentExamId: data.examId,
    };

    setFormDefaults(formDefaults);
    setIsOpenAddExamForm(true);
  };

  const handleUnappointedExam = (props: AssistantExamResponse) => {
    Swal.fire({
      title: t('courses.exam.calendar.examPopupTitle'),

      html: `
         <div class="event-popup">
      
           <div class="info-grid">
             <div class="label">${t('courses.exam.calendar.eventCourse')}:</div>
             <div class="value">${props.course.name ?? '—'}</div>
     
              <div class="label">${t('courses.exam.calendar.eventCategory')}:</div>
             <div class="value">${props.lesson.category.name}</div>
     
             <div class="label">${t('courses.exam.calendar.eventLesson')}:</div>
             <div class="value">${props.lesson.title}</div>
       
             <div class="label">${t('courses.exam.calendar.eventDate')}:</div>
             <div class="value">${props.examDate ? new Date(props.examDate).toLocaleDateString('uk-UA') : '—'}</div>
       
             <div class="label">${t('courses.exam.calendar.eventTime')}:</div>
             <div class="value">${
               props.examDate
                 ? new Date(props.examDate).toLocaleTimeString('uk-UA', {
                     hour: '2-digit',
                     minute: '2-digit',
                   })
                 : '—'
             }</div>
       
             <div class="label">${t('courses.exam.calendar.eventDescription')}:</div>
             <div class="value">${props.lesson.description}</div>
       
             <div class="label f-w-400">Email:</div>
             <div class="value">${props.user.email}</div>
     
             <div class="label f-w-400">${t('courses.exam.student')}:</div>
             <div class="value">${props.user.firstName} ${props.user.lastName}</div>
     
             <div class="label">${t('courses.exam.examLanguage')}:</div>
             <div class="value">${props.examLang ? props.examLang : t('courses.exam.examLangNotSelected')}</div>
     
           </div>
        
         </div>
       `,
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: Primary,
      confirmButtonText: t('courses.exam.calendar.eventRemoveEvent'),
      cancelButtonText: t('courses.exam.calendar.eventCloseEvent'),
      showDenyButton: true,
      denyButtonText: t('exams.createExam'),
      denyButtonColor: Secondary,
      didOpen: () => {
        const el = Swal.getHtmlContainer();
        if (!el) return;

        // Delegate clicks on router links
        el.addEventListener('click', (e) => {
          const target = e.target as HTMLElement;
          const a = target.closest('a[data-router-link]') as HTMLAnchorElement | null;
          if (!a) return;

          // allow new-tab/middle-clicks to behave normally
          const mouseEvent = e as MouseEvent;
          if (
            mouseEvent.metaKey ||
            mouseEvent.ctrlKey ||
            mouseEvent.shiftKey ||
            mouseEvent.button === 1
          ) {
            return;
          }

          e.preventDefault();
          const href = a.getAttribute('href');

          if (href) {
            navigate(href);
            Swal.close();
          } else if (href) {
            window.location.assign(href);
          }
        });
      },
      willClose: () => {
        cleanupBodyLocks();
      },
    }).then((result) => {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // exam date may be string/Date; normalize & validate
      const examDate = props.examDate ? new Date(props.examDate) : null;
      const isValidExamDate = examDate instanceof Date && !isNaN(examDate.getTime());
      const isPast = isValidExamDate ? examDate < startOfToday : true; // treat invalid as past

      if (result.isConfirmed) {
        deleteExamCalendarHandler({
          examId: props.id,
          userId: props.user.id,
          examDate: props.examDate,
        });
      } else if (result.isDenied) {
        Swal.close();
        requestAnimationFrame(() => {
          if (isPast) {
            Swal.fire({
              title: t('courses.exam.calendar.pastDateTitle') || 'Past date',
              text:
                t('courses.exam.calendar.pastDateText') ||
                'You can add an exam only for today or a future date.',
              icon: 'warning',
              confirmButtonColor: Primary,
            });
            return;
          } else {
            const formDefaults = {
              userId: props.user.id,
              userName: `${props.user.firstName} ${props.user.lastName}`,
              courseId: props.course.id,
              lessonId: props.lesson.id,
              examDate: props.examDate,
              prompt: questionPrompt,
              examId: props.id,
            };

            handleOpenAddExamForm(formDefaults);
          } // <-- run the callback
        });
      }
      // cancel: do nothing
    });
  };
  useEffect(() => {
    return () => {
      revokeObjectUrls(previews);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleDeleteFile = async (p: PreviewItem) => {
    Swal.fire({
      title: t('global.filesMutationsText.titleDelete'),
      text: t('global.filesMutationsText.textDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('global.filesMutationsText.delete'),
      cancelButtonText: t('global.cancel'),
    }).then((result) => {
      if (result.isConfirmed) {
        setToDeleteFileUrl((prev) => (prev ? [...prev, p.url] : [p.url]));
        handleRemovePreview(p);
      } else if (result.isDismissed) {
        setToDeleteFileUrl((prev) => (prev ? [...prev] : []));
        return;
      }
    });
  };
  return (
    <>
      {' '}
      <Breadcrumbs
        mainTitle={t('leads.examinations.title')}
        tooltip={<ExaminationsHeadingTooltip />}
      />
      <Container fluid>
        <Card>
          <CardBody>
            {/* <CommonCardHeader title={t('leads.examinations.tabsTitle')} /> */}
            <Nav tabs className="simple-wrapper mt-3">
              {examinationsTabs.map((item, index) => (
                <NavItem key={index}>
                  <NavLink
                    tag="button"
                    type="button"
                    className={`txt-info !h4 f-w-500 ${basicTab === `${index + 1}` ? 'active' : ''}`}
                    onClick={() => {
                      setBasicTab(`${index + 1}`);
                      localStorage.setItem('stored-tabs', `${index + 1}`);
                    }}
                  >
                    {t(`leads.examinations.${item}_withCount`, { count: tabsTotals[item as keyof typeof tabsTotals] })}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <TabContent activeTab={basicTab}>
              <TabPane className={`fade ${basicTab === '1' ? 'show' : ''}`} tabId={'1'} key={1}>
                <UnsetExaminations
                  handleModalOpen={handleModalOpen}
                  handleUnappointedExam={handleUnappointedExam}
                  setTabsTotals={setTabsTotals}
                />
              </TabPane>
              <TabPane className={`fade ${basicTab === '2' ? 'show' : ''}`} tabId={'2'} key={2}>
                <FutureExaminations
                  handleModalOpen={handleModalOpen}
                  setTabsTotals={setTabsTotals}
                />
              </TabPane>
              <TabPane className={`fade ${basicTab === '3' ? 'show' : ''}`} tabId={'3'} key={3}>
                <PastExaminations handleModalOpen={handleModalOpen} setTabsTotals={setTabsTotals} />
              </TabPane>
            </TabContent>
          </CardBody>{' '}
        </Card>
      </Container>
      <CommentFileModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        labels={{
          title: t('leads.commentForm.label'),
          placeholder: t('leads.commentForm.placeholder'),
          ok: t('global.ok'),
          saving: t('global.saving'),
          cancel: t('global.cancel'),
          remove: t('common.remove'),
          examHandled: t('leads.examHandled'),
          yes: t('exams.yes'),
          no: t('exams.no'),
        }}
        commentRef={commentRef as React.RefObject<HTMLTextAreaElement>}
        comment={comment}
        hasFileInput={true}
        onCommentChange={setComment}
        fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        onFilesSelected={handleFilesSelected}
        previews={previews}
        onRemovePreview={handleRemovePreview}
        uploadErrors={uploadErrors}
        submitting={submitting}
        onSubmit={handleSubmitComment}
        isExamPassed={isExamPassed}
        setIsExamPassed={setIsExamPassed}
        onFileDelete={handleDeleteFile}
      />
      <SimpleModal isOpen={isOpenAddExamForm} close={() => setIsOpenAddExamForm(false)} size={'xl'}>
        <CreateExamForm
          defaults={formDefaults}
          close={() => setIsOpenAddExamForm(false)}
          useDefaults={true}
          createAssistantExam={async (payload) => {
            const res = await createAssistantExam(payload).unwrap();
            // await refetchExamsData(); // <- force refresh immediately
            return res;
            // setIsOpenAddExamForm(false);   // close the modal
          }}
        />
      </SimpleModal>
    </>
  );
};
export default Examinations;
